import { useSilver } from "@/context/SilverContext";
import { MOSAIC_BY_CODE, RETIREMENT_STAGES, BEDROOMS, SIGNALS } from "@/data/mosaic";
import { Reveal, DataTip } from "@/components/silver/common/Reveal";
import { fmtInt } from "@/components/silver/common/AnimatedNumber";
import { cn } from "@/lib/utils";

const pct = (v, t) => (t ? Math.round((v / t) * 100) : 0);
const tip = (label, v, t) => `${label} · ${fmtInt(v)} households (${pct(v, t)}%)`;

function StageBar({ a, total, focus }) {
  return (
    <div>
      <p className="eyebrow-muted">Life stage</p>
      <div className="mt-4 flex h-14 overflow-hidden rounded-2xl border border-white/10">
        {RETIREMENT_STAGES.map((s, i) => {
          const v = a.retirement[s.key];
          const dim = focus && focus.retirement !== s.key;
          return (
            <DataTip key={s.key} label={tip(s.label, v, total)}>
              <div tabIndex={0} className={cn("relative flex items-center overflow-hidden px-4 transition-[flex-grow,opacity] duration-1000 ease-out", i === 0 ? "bg-gradient-to-r from-[#7AA818] to-[#96C11F] text-[#12290F]" : "bg-[repeating-linear-gradient(135deg,rgba(198,238,90,0.18)_0_6px,rgba(198,238,90,0.08)_6px_12px)] text-white", dim && "opacity-30")}
                style={{ flexGrow: v || 0.0001, flexBasis: 0 }} data-testid={`stage-segment-${s.key}`}>
                {pct(v, total) >= 14 && <span className="whitespace-nowrap font-ui text-[12px] font-bold">{s.label} · {pct(v, total)}%</span>}
              </div>
            </DataTip>
          );
        })}
      </div>
    </div>
  );
}

function PropertySkyline({ a, total, focus }) {
  const max = Math.max(1, ...BEDROOMS.map((b) => a.property[b]));
  return (
    <div>
      <p className="eyebrow-muted">Property size</p>
      <div className="mt-4 grid h-[190px] grid-cols-4 items-end gap-3 sm:gap-5">
        {BEDROOMS.map((b) => {
          const v = a.property[b];
          const h = Math.max(4, (v / max) * 130);
          const on = focus ? focus.bedrooms === b : true;
          return (
            <DataTip key={b} label={tip(`${b} bed`, v, total)}>
              <div tabIndex={0} className={cn("flex flex-col items-center transition-opacity duration-500", !on && "opacity-30")} data-testid={`property-column-${b}`}>
                <span className="num mb-2 font-ui text-[12px] font-semibold text-white/80">{pct(v, total)}%</span>
                <svg viewBox="0 0 60 14" className="w-full max-w-[72px]" aria-hidden="true"><path d="M2 14 L30 1 L58 14 Z" fill={on && focus ? "#C6EE5A" : "rgba(150,193,31,0.75)"} /></svg>
                <div className="w-full max-w-[72px] rounded-b-md bg-gradient-to-b from-[#96C11F]/80 to-[#96C11F]/10 transition-[height] duration-1000 ease-out" style={{ height: h }} />
                <span className="mt-3 font-ui text-[11px] font-semibold uppercase tracking-[0.12em] text-white/60">{b} bed</span>
              </div>
            </DataTip>
          );
        })}
      </div>
    </div>
  );
}

function SignalMeters({ a, total, focus }) {
  return (
    <div>
      <p className="eyebrow-muted">Opportunity signals <span className="normal-case tracking-normal text-white/35">— overlapping</span></p>
      <div className="mt-4 space-y-4">
        {SIGNALS.map((s) => {
          const v = a.signals[s.key];
          const on = focus ? focus[s.key] : true;
          return (
            <DataTip key={s.key} label={tip(s.label, v, total)}>
              <div tabIndex={0} className={cn("transition-opacity duration-500", !on && "opacity-30")} data-testid={`signal-meter-${s.key}`}>
                <div className="flex items-baseline justify-between text-[13px]">
                  <span className="font-semibold text-white/85">{s.label}</span>
                  <span className="num font-ui text-white/55">{fmtInt(v)} · {pct(v, total)}%</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/[0.07]">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#7AA818] to-[#C6EE5A] transition-[width] duration-1000 ease-out" style={{ width: `${pct(v, total)}%` }} />
                </div>
              </div>
            </DataTip>
          );
        })}
      </div>
    </div>
  );
}

export function CompositionPanel() {
  const { audience, hoveredGroup } = useSilver();
  const a = audience.attributes;
  const focus = hoveredGroup ? MOSAIC_BY_CODE[hoveredGroup] : null;
  return (
    <Reveal className="glass p-7 sm:p-10" data-testid="composition-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow">Audience composition</p>
          <h3 className="mt-3 text-3xl font-extrabold tracking-[-0.04em]">Who makes up your audience.</h3>
        </div>
        <p className="min-h-[20px] font-ui text-[12px] text-[#C6EE5A]" aria-live="polite">{focus ? `Highlighting ${focus.name}` : ""}</p>
      </div>
      <div className="mt-9 grid gap-10 lg:grid-cols-2">
        <div className="space-y-10">
          <StageBar a={a} total={a.total} focus={focus} />
          <SignalMeters a={a} total={a.total} focus={focus} />
        </div>
        <PropertySkyline a={a} total={a.total} focus={focus} />
      </div>
      <table className="sr-only">
        <caption>Audience composition</caption>
        <tbody>
          {RETIREMENT_STAGES.map((s) => <tr key={s.key}><th>{s.label}</th><td>{a.retirement[s.key]}</td></tr>)}
          {BEDROOMS.map((b) => <tr key={b}><th>{b} bed</th><td>{a.property[b]}</td></tr>)}
          {SIGNALS.map((s) => <tr key={s.key}><th>{s.label}</th><td>{a.signals[s.key]}</td></tr>)}
        </tbody>
      </table>
    </Reveal>
  );
}
